import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, PhoneIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../components/MobileLayout';
import SmsConsentForm from '../components/SmsConsentForm';
import SmsConsentSettings from '../components/SmsConsentSettings';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { SMS_REACHOUT_PROGRAM_CREDITS } from '../constants/sms.constants';

interface UserProfile {
  phoneNumber?: string;
  phoneVerified?: boolean;
  smsConsentGiven?: boolean;
  reachoutProgramEnabled?: boolean;
}

const PhoneSettings: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReachoutForm, setShowReachoutForm] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/users/profile');
      setProfile(response.data);
      setNewPhoneNumber(response.data.phoneNumber || '');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhone = async () => {
    if (!newPhoneNumber.trim()) {
      toast.error(t('phoneSettings.phoneRequired', 'Phone number is required'));
      return;
    }

    // Validate US phone number
    const digits = newPhoneNumber.replace(/\D/g, '');
    if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith('1')))) {
      toast.error(
        t(
          'phoneSettings.usOnly',
          'Only US phone numbers are supported. Please enter a valid 10-digit number.',
        ),
      );
      return;
    }

    setUpdating(true);
    try {
      await axios.put('/users/profile', {
        phoneNumber: newPhoneNumber,
      });
      await fetchProfile();
      setEditingPhone(false);
      toast.success(t('phoneSettings.updateSuccess', 'Phone number updated successfully'));
    } catch (error: any) {
      console.error('Failed to update phone:', error);
      toast.error(
        error.response?.data?.message ||
          t('phoneSettings.updateError', 'Failed to update phone number. Please try again.'),
      );
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-600 dark:text-gray-400">
            {t('common.loading', 'Loading...')}
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col bg-light-bg dark:bg-dark-bg">
        {/* Header */}
        <div className="p-4 border-b border-light-border dark:border-dark-border flex items-center gap-3">
          <button
            onClick={() => navigate('/profile')}
            className="text-light-text dark:text-dark-text"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold text-light-text dark:text-dark-text">
            {t('phoneSettings.title', 'Phone Number & SMS')}
          </h1>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Current Phone Number */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <PhoneIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('phoneSettings.phoneNumber', 'Phone Number')}
              </h2>
            </div>

            {!editingPhone ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {t('phoneSettings.currentPhone', 'Current Phone')}
                  </p>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    {profile?.phoneNumber || (
                      <span className="text-gray-400">{t('phoneSettings.noPhone', 'Not set')}</span>
                    )}
                  </p>
                  {profile?.phoneVerified && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      ✓ {t('phoneSettings.verified', 'Verified')}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setEditingPhone(true)}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors"
                >
                  {profile?.phoneNumber
                    ? t('phoneSettings.changePhone', 'Change Phone Number')
                    : t('phoneSettings.addPhone', 'Add Phone Number')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('phoneSettings.newPhone', 'New Phone Number')}
                  </label>
                  <input
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleUpdatePhone}
                    disabled={updating}
                    className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                  >
                    {updating ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingPhone(false);
                      setNewPhoneNumber(profile?.phoneNumber || '');
                    }}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* SMS Reachout Program */}
          {profile?.phoneNumber && !profile?.reachoutProgramEnabled && (
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 shadow-lg text-white">
              <h2 className="text-xl font-bold mb-2">
                {t(
                  'phoneSettings.reachoutTitle',
                  `🎉 Get ${SMS_REACHOUT_PROGRAM_CREDITS} Free Credits!`,
                )}
              </h2>
              <p className="mb-4 opacity-90">
                {t(
                  'phoneSettings.reachoutDescription',
                  'Enroll in our SMS Reachout Program and receive messages from your companion via SMS!',
                )}
              </p>
              {!showReachoutForm ? (
                <button
                  onClick={() => setShowReachoutForm(true)}
                  className="bg-white text-purple-600 font-semibold py-2 px-6 rounded-md hover:bg-gray-100 transition-colors"
                >
                  {t('phoneSettings.enrollNow', 'Enroll Now')}
                </button>
              ) : (
                <div className="mt-4">
                  <SmsConsentForm
                    type="reachout_program"
                    onSuccess={() => {
                      setShowReachoutForm(false);
                      fetchProfile();
                    }}
                    onCancel={() => setShowReachoutForm(false)}
                  />
                </div>
              )}
            </div>
          )}

          {/* SMS Consent Management */}
          {profile?.smsConsentGiven && (
            <div>
              <SmsConsentSettings />
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default PhoneSettings;
